import { Axis } from '../axis/axis.class';
import { ControllerRootOptions } from '../../controller-classes';
import { nearestSixteenth } from '../../helpers';
import { translateYCfn } from '../functions';
import { vertex3Data } from './vertex-3.data';
import { addCfn, distance3, move3OnAxisCfn, rotate3AxisCfn, rotate3Cfn, subtractCfn } from './functions';
import { Vertex3Data, Vertex3DataIn } from './types';

export class Vertex3 {
	private _x: number;
	private _y: number;
	private _z: number;
	private _readOnly: boolean;

	public get x (): number {
		return this._x;
	}

	public set x (value: number) {
		this.checkReadOnly();
		this._x = value;
	}

	public get y (): number {
		return this._y;
	}

	public set y (value: number) {
		this.checkReadOnly();
		this._y = value;
	}

	public get z (): number {
		return this._z;
	}

	public set z (value: number) {
		this.checkReadOnly();
		this._z = value;
	}

	constructor (options: ControllerRootOptions<Vertex3DataIn> = {}) {
		// super(BlueprintInstanceType.Point, options);

		const { dataIn = null } = options;
		const data = vertex3Data(dataIn);

		this._x = data.x ?? 0;
		this._y = data.y ?? 0;
		this._z = data.z ?? 0;
		this._readOnly = data.readOnly ?? false;
	}

	/**
	 * Gets the read-only state of the vertex
	 * @returns true if the vertex is read-only, false otherwise
	 */
	public get readOnly (): boolean {
		return this._readOnly;
	}

	/**
	 * Sets the read-only state of the vertex
	 * @param value true to make read-only, false to allow updates
	 */
	public set readOnly (value: boolean) {
		this._readOnly = value;
	}

	/**
	 * Throws an error if the vertex is read-only
	 * @private
	 */
	private checkReadOnly (): void {
		if (this._readOnly) {
			throw new Error('Cannot modify read-only vertex');
		}
	}

	/**
	 * inverts x, y and z values
	 * @returns This Point
	 */
	public invert (): Vertex3 {
		this.checkReadOnly();
		this._x *= -1;
		this._y *= -1;
		this._z *= -1;

		return this;
	}

	/**
	 * Moves the point to the specified point
	 * @param point Point to move to
	 * @returns This Point
	 */
	public moveTo (point: Vertex3): Vertex3 {
		this.checkReadOnly();
		this._x = point.x;
		this._y = point.y;
		this._z = point.z;

		return this;
	}

	/**
	 * Rounds the point to the nearest sixteenth
	 * @returns This Point
	 */
	public roundNearestSixteenth (): Vertex3 {
		this.checkReadOnly();
		this._x = nearestSixteenth(this._x);
		this._y = nearestSixteenth(this._y);
		this._z = nearestSixteenth(this._z);

		return this as Vertex3;
	}

	/**
	 * Rotates the point around the y axis
	 *
	 * @param angle Angle in degrees to rotate point
	 * @param originIn Origin point to use for rotation, optional defaults to {x: 0, y: 0, z:0}
	 */
	public translateY (angle: number, originIn: Vertex3 | null = null): void {
		this.checkReadOnly();
		translateYCfn(this as Vertex3, angle, originIn);
	}

	public clone (): Vertex3 {
		return new Vertex3({ dataIn: this.serialize() })
	}

	public serialize (): Vertex3Data {
		return {
			x: this._x,
			y: this._y,
			z: this._z
		};
	}

	public add (distance: Vertex3): Vertex3 { return addCfn(this as Vertex3, distance); }
	public moveOnAxis (distance: number, axis: Axis): Vertex3 { return move3OnAxisCfn(this as Vertex3, distance, axis); }
	public subtract (distance: Vertex3): Vertex3 { return subtractCfn(this as Vertex3, distance); }
	public rotateAxis (origin: Vertex3, angle: number, axis: string): Vertex3 { return rotate3AxisCfn(this as Vertex3, origin, angle, axis) }
	public rotate (origin: Vertex3, axis: Axis): Vertex3 { return rotate3Cfn(this as Vertex3, origin, axis) }

	public getDistance (point: Vertex3): number { return distance3(this as Vertex3, point); }
}
